import { getSessionStorage, mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

// Mobile sizes tail of the card images (snippets/util-autofill-img-size-attr.liquid): two
// columns by default, one column after the density toggle. Chromium picks lazy cards by
// sizes="auto", Safari/Firefox only by this tail - so the toggle rewrites it.
const SIZES_TWO_COLUMNS = 'calc(50vw - 16px)';
const SIZES_ONE_COLUMN = 'calc(100vw - 14px)';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#sizesObserver.disconnect();
  }

  #sizesObserver = new MutationObserver(() => this.#syncImageSizes());

  /**
   * Points the card images' mobile sizes tail at the current column count.
   */
  #syncImageSizes() {
    const { grid } = this.refs;
    if (!grid) return;

    const oneColumn = grid.getAttribute('product-grid-view') === 'mobile-single';
    const [from, to] = oneColumn ? [SIZES_TWO_COLUMNS, SIZES_ONE_COLUMN] : [SIZES_ONE_COLUMN, SIZES_TWO_COLUMNS];

    for (const img of grid.querySelectorAll('img[sizes]')) {
      const sizes = img.getAttribute('sizes') || '';
      if (sizes.endsWith(from)) img.setAttribute('sizes', sizes.slice(0, -from.length) + to);
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      const storage = getSessionStorage();
      if (!storage) return;
      try {
        storage.setItem(`product-grid-view-${viewport}`, value);
      } catch {
        // ignore
      }
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#syncImageSizes();
    // Pagination and filter updates insert cards with the two-column tail; only worth
    // watching once the visitor left the default view.
    if (value === 'mobile-single') this.#sizesObserver.observe(this, { childList: true, subtree: true });
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  if (!customElements.get('results-list')) customElements.define('results-list', ResultsList);
}
